import { useMemo, useEffect, useContext } from "react";
import { useDropzone } from "react-dropzone";

import Icon from "@mui/material/Icon";
import { useTheme } from "@mui/material/styles";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { UserContext } from "context/AppProvider/UserContext";

const baseStyle = {
   flex: 1,
   display: "flex",
   flexDirection: "column",
   alignItems: "center",
   padding: "20px",
   borderWidth: 2,
   borderRadius: 2,
   borderColor: "#eeeeee",
   borderStyle: "dashed",
   backgroundColor: "#fafafa",
   color: "#bdbdbd",
   outline: "none",
   transition: "border .24s ease-in-out"
};

const focusedStyle = {
   borderColor: "#2196f3"
};

const acceptStyle = {
   borderColor: "#00e676"
};

const rejectStyle = {
   borderColor: "#ff1744"
};

const requiredProps = ["created_by", "created_by_id", "description", "group_name", "group_id", "name", "notes", "platform_name", "platform_type", "shared_link", "shared_status", "trashed", "steps"];

const ImportFlowDropzone = ({ setFileToImport, fileError, setFileError }) => {
   const { userData } = useContext(UserContext);
   const theme = useTheme();
   const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject, acceptedFiles } = useDropzone({
      accept: { "application/json": [] },
      multiple: true
   });

   const validateFileProps = (file) => {
      const reader = new FileReader();

      reader.onload = (e) => {
         const jsonData = JSON.parse(e.target.result);
         let missingProps = [];
         requiredProps.forEach((prop) => {
            Object.keys(jsonData)?.includes(prop) ? null : missingProps.push(prop);
         });

         if (missingProps.length > 0) {
            setFileError({
               path: file.path,
               code: "missing-props",
               message: "The following properties are missing: " + JSON.stringify(missingProps.join())
            });
            setFileToImport(null);
         } else {
            setFileToImport((prevFiles) => [
               ...prevFiles,
               {
                  ...jsonData,
                  created_by: `${userData.first_name} ${userData.last_name}`,
                  created_by_id: userData.auth0_id,
                  group_name: "",
                  group_id: "",
                  shared_link: "",
                  trashed: false,
                  tenant_id: userData.tenant_id,
                  shared_status: "test"
               }
            ]);
            setFileError(null);
         }
      };

      reader.readAsText(file);
   };

   useEffect(() => {
      if (acceptedFiles.length > 0) {
         acceptedFiles.forEach((file) => {
            validateFileProps(file);
         });
      }
   }, [acceptedFiles]);

   const style = useMemo(
      () => ({
         ...baseStyle,
         backgroundColor: theme.palette.mode === "dark" ? "#000000" : "#ffffff",
         borderColor: theme.palette.mode === "dark" ? "#6c757d" : "#d2d6da",
         borderStyle: "dotted",
         ...(isFocused ? focusedStyle : {}),
         ...(isDragAccept ? acceptStyle : {}),
         ...(isDragReject ? rejectStyle : {})
      }),
      [theme.palette.mode, isFocused, isDragAccept, isDragReject]
   );

   return (
      <SoftBox className="container">
         <SoftBox {...getRootProps({ style })} sx={{ mb: 2 }}>
            <input {...getInputProps()} />
            {isDragReject ? <em>(Only JSON files are accepted here)</em> : <p>Drag &apos;n&apos; drop files here, or click to select files to import</p>}
         </SoftBox>
         <SoftBox>
            {!fileError &&
               acceptedFiles?.map((file, index) => (
                  <SoftBox key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                     <Icon>text_snippet_icon</Icon>
                     <SoftTypography variant="caption" fontSize="medium">
                        {file.path}
                     </SoftTypography>
                  </SoftBox>
               ))}
            {fileError && (
               <SoftBox>
                  <SoftBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                     <Icon color="error">text_snippet_icon</Icon>
                     <SoftTypography variant="caption" fontSize="medium" color="error">
                        {fileError.path}
                     </SoftTypography>
                  </SoftBox>
                  <SoftTypography variant="caption" color="error" pl={2}>
                     - {fileError.message}
                  </SoftTypography>
               </SoftBox>
            )}
         </SoftBox>
      </SoftBox>
   );
};

export default ImportFlowDropzone;
